import { useState, useEffect, useCallback } from "react";
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { getCartItems, setCartItems as saveMainCart } from '@/lib/sessionUtils';
import { CartItem } from '@/types/quiz';

interface CollectionProduct {
  id: string;
  name: string;
  slug: string;
  price_aed: number;
  price_50ml: number | null;
  price_100ml: number | null;
  sample_price_aed: number | null;
  has_sample: boolean;
  images?: Array<{
    image_url: string;
    is_primary: boolean;
  }>;
}

interface CollectionCartItem {
  id: string;
  user_id: string;
  product_id: string;
  quantity: number;
  size_ml: number;
  is_sample: boolean;
  created_at: string;
  product?: CollectionProduct;
}

const MAX_FREE_SAMPLES = 4;

export const useCollectionCart = () => {
  const [dbCartItems, setDbCartItems] = useState<CollectionCartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { user } = useAuth();

  // Fetch collection items from database for the current user
  const fetchDbCart = useCallback(async (): Promise<CollectionCartItem[]> => {
    if (!user) {
      setDbCartItems([]);
      setLoading(false);
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('collection_cart')
        .select(`
          *,
          product:collection_products(
            id, name, slug, price_aed, price_50ml, price_100ml, sample_price_aed, has_sample,
            images:collection_product_images(image_url, is_primary)
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      const items = (data || []) as CollectionCartItem[];
      setDbCartItems(items);
      return items;
    } catch (error) {
      console.error('Error fetching collection cart:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchDbCart();
  }, [fetchDbCart]);

  // Listen for cartUpdated events from other components (e.g. CartDrawer)
  useEffect(() => {
    const handler = () => { fetchDbCart(); };
    window.addEventListener('cartUpdated', handler);
    return () => window.removeEventListener('cartUpdated', handler);
  }, [fetchDbCart]);

  // Dispatch cartUpdated event so all hook instances re-fetch
  const dispatchCartUpdated = () => {
    window.dispatchEvent(new Event('cartUpdated'));
  };

  // Count total samples in DB cart
  const getSampleCount = useCallback((): number => {
    return dbCartItems
      .filter(item => item.is_sample)
      .reduce((sum, item) => sum + item.quantity, 0);
  }, [dbCartItems]);

  // Get price for an item based on size and sample status
  const getItemPrice = (productId: string, sizeMl: number, isSample: boolean): number => {
    const dbItem = dbCartItems.find(item => 
      item.product_id === productId && 
      item.size_ml === sizeMl && 
      item.is_sample === isSample
    );
    if (!dbItem?.product) return 0;
    if (isSample) return 0;
    if (sizeMl === 50) return dbItem.product.price_50ml || dbItem.product.price_aed;
    return dbItem.product.price_100ml || dbItem.product.price_aed;
  };

  // Add to cart — requires authentication (no guest flow)
  const addToCart = async (
    productId: string,
    quantity = 1,
    sizeMl = 100,
    isSample = false
  ): Promise<boolean | 'already_in_cart'> => {
    // Auth gate: must be logged in
    if (!user) {
      return false;
    }

    // Check if exact product+size+sample combo already in cart
    const existingItem = dbCartItems.find(
      item => item.product_id === productId && item.size_ml === sizeMl && item.is_sample === isSample
    );
    // Samples are restricted to 1 per product — block duplicates & cap quantity to 1
    if (isSample) {
      const alreadyHasSample = dbCartItems.some(
        item => item.product_id === productId && item.is_sample
      );
      if (alreadyHasSample) {
        toast({
          title: 'Maximum quantity reached',
          description: 'You can only add 1 sample per product.',
          duration: 1500,
        });
        return 'already_in_cart';
      }
      // Enforce global sample limit
      const currentSamples = getSampleCount();
      if (currentSamples + 1 > MAX_FREE_SAMPLES) {
        toast({
          title: 'Sample limit reached',
          description: `You have reached the limit of ${MAX_FREE_SAMPLES} free samples.`,
          variant: 'destructive',
        });
        return false;
      }
    }
    // For regular products, if already in cart, update quantity instead
    if (existingItem && !isSample) {
      try {
        const { error } = await supabase
          .from('collection_cart')
          .update({ quantity: existingItem.quantity + quantity })
          .eq('id', existingItem.id);
        if (error) throw error;
        const freshItems = await fetchDbCart();
        syncWithMainCart(freshItems);
        dispatchCartUpdated();
        return true;
      } catch (error: any) {
        toast({ title: 'Error', description: error.message, variant: 'destructive' });
        return false;
      }
    }


    // === SIGNED-IN flow (only flow now) ===
    try {
      const { data: product, error: productError } = await supabase
        .from('collection_products')
        .select(`
          id, name, slug, price_aed, price_50ml, price_100ml, sample_price_aed, has_sample,
          images:collection_product_images(image_url, is_primary)
        `)
        .eq('id', productId)
        .single();

      if (productError || !product) throw new Error('Product not found');

      // Look for existing item - also match size_ml=0 (legacy rows) as equivalent
      const { data: existingDbItem } = await supabase
        .from('collection_cart')
        .select('id, quantity, size_ml')
        .eq('user_id', user.id)
        .eq('product_id', productId)
        .eq('is_sample', isSample)
        .in('size_ml', [sizeMl, 0])
        .limit(1)
        .single();

      if (existingDbItem) {
        await supabase
          .from('collection_cart')
          .update({ quantity: existingDbItem.quantity + quantity, size_ml: sizeMl })
          .eq('id', existingDbItem.id);
      } else {
        await supabase
          .from('collection_cart')
          .insert({
            user_id: user.id,
            product_id: productId,
            quantity,
            size_ml: sizeMl,
            is_sample: isSample,
          });
      }

      toast({ title: 'Added to cart' });
      const freshItems = await fetchDbCart();
      syncWithMainCart(freshItems);
      dispatchCartUpdated();
      return true;
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
      return false;
    }
  };

  // Update quantity — DB only (samples capped at 1)
  const updateQuantity = async (cartItemId: string, quantity: number) => {
    if (quantity < 1) {
      return removeFromCart(cartItemId);
    }

    // Enforce sample quantity cap
    const cartItem = dbCartItems.find(item => item.id === cartItemId);
    if (cartItem?.is_sample && quantity > 1) {
      toast({
        title: 'Maximum quantity reached',
        description: 'You can only add 1 sample per product.',
        duration: 1500,
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('collection_cart')
        .update({ quantity })
        .eq('id', cartItemId);

      if (error) throw error;

      const freshItems = await fetchDbCart();
      syncWithMainCart(freshItems);
      dispatchCartUpdated();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  // Remove — DB only
  const removeFromCart = async (cartItemId: string) => {
    try {
      const { error } = await supabase
        .from('collection_cart')
        .delete()
        .eq('id', cartItemId);

      if (error) throw error;

      toast({ title: 'Removed from cart' });
      const freshItems = await fetchDbCart();
      syncWithMainCart(freshItems);
      dispatchCartUpdated();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  // Clear all collection items
  const clearCart = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('collection_cart')
        .delete()
        .eq('user_id', user.id);

      if (error) throw error;
      
      const currentCart = getCartItems();
      const updatedCart = currentCart.filter((item: CartItem) => !item.isCollectionItem);
      saveMainCart(updatedCart);
      
      setDbCartItems([]);
      dispatchCartUpdated();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  // Clear collection items from main cart only (used after checkout)
  const clearFromMainCart = () => {
    const currentCart = getCartItems();
    const updatedCart = currentCart.filter((item: CartItem) => !item.isCollectionItem);
    saveMainCart(updatedCart);
  };

  // Sync database cart with main cart — clears all collection items then rebuilds from DB
  const syncWithMainCart = (items?: CollectionCartItem[]) => {
    const cartItems = items || dbCartItems;
    if (!user) return;

    const currentCart = getCartItems();
    const nonCollectionItems = currentCart.filter((item: CartItem) => !item.isCollectionItem);

    for (const dbItem of cartItems) {
      if (!dbItem.product) continue;

      const primaryImage = dbItem.product.images?.find((img: any) => img.is_primary) || dbItem.product.images?.[0];
      const price = dbItem.is_sample 
        ? 0
        : dbItem.size_ml === 50 
          ? dbItem.product.price_50ml || dbItem.product.price_aed
          : dbItem.product.price_100ml || dbItem.product.price_aed;

      nonCollectionItems.push({
        id: `collection-${dbItem.id}`,
        type: 'collection',
        recipientName: '',
        country: '',
        quantity: dbItem.quantity,
        price: price * dbItem.quantity,
        isCollectionItem: true,
        collectionProductId: dbItem.product_id,
        collectionProductName: dbItem.product.name,
        collectionProductSlug: dbItem.product.slug,
        collectionProductImage: primaryImage?.image_url || null,
        sizeMl: dbItem.size_ml,
        isSample: dbItem.is_sample,
      } as CartItem);
    }

    saveMainCart(nonCollectionItems);
  };

  // Get price for a db cart item — samples are free
  const getDbItemPrice = (item: CollectionCartItem): number => {
    if (!item.product) return 0;
    if (item.is_sample) return 0;
    if (item.size_ml === 50) return item.product.price_50ml || item.product.price_aed;
    return item.product.price_100ml || item.product.price_aed;
  };

  // Cart items are always from DB (single source of truth)
  const allCartItems = dbCartItems;

  const cartTotal = allCartItems.reduce((total, item) => {
    return total + (getDbItemPrice(item) * item.quantity);
  }, 0);

  const cartCount = allCartItems.reduce((count, item) => count + item.quantity, 0);

  const isInCart = useCallback((productId: string, sizeMl: number, isSample: boolean): boolean => {
    return dbCartItems.some(
      item => item.product_id === productId && item.size_ml === sizeMl && item.is_sample === isSample
    );
  }, [dbCartItems]);

  // Find the cart item ID for a given product+size+sample combo
  const getCartItemId = useCallback((productId: string, sizeMl: number, isSample: boolean): string | null => {
    const found = dbCartItems.find(
      item => item.product_id === productId && item.size_ml === sizeMl && item.is_sample === isSample
    );
    return found ? found.id : null;
  }, [dbCartItems]);

  const sampleCount = getSampleCount();

  return {
    cartItems: allCartItems as CollectionCartItem[],
    loading,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    clearFromMainCart,
    getItemPrice: (item: CollectionCartItem) => {
      if (item.is_sample) return 0;
      return getDbItemPrice(item);
    },
    cartTotal,
    cartCount,
    sampleCount,
    isInCart,
    getCartItemId,
    refetch: fetchDbCart,
    syncWithMainCart,
  };
};
